import {
	Checkbox,
	ComboBox,
	FlexColumn,
	FlexRow,
	Icon,
	IconTooltip,
	Label,
	Modal,
	Separator,
	Text,
	TextField,
} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import {useClientContactForm} from './hooks/useClientContactForm';
import {GuestUserField, LabelWithTooltipField, ProjectBadge, ProjectListItem, RemoveButton} from './ClientContactModal.styles';
import ViewMoreLess from '@/components/view-more-less/view-more-less';

export type ClientContactModalMode = 'contact' | 'guest-user';
export type ClientContactModalEntryPoint = 'client-page' | 'header';
export type ClientOption = {id: string; name: string};
export type ProjectOption = {id: string; name: string; prefix?: string; color?: string};

export type ClientContactModalProps = {
	mode: ClientContactModalMode;
	entryPoint: ClientContactModalEntryPoint;
	clientId?: string;
	clientOptions: ClientOption[];
	projectOptions: ProjectOption[];
	isClientsLoading: boolean;
	isProjectsLoading: boolean;
	isGuestUser: boolean;
	onGuestUserToggle: () => void;
	onClientChange: (clientId: string) => void;
	onClose: () => void;
};

const ClientContactModal = ({
	mode,
	clientId = '',
	clientOptions,
	projectOptions,
	isClientsLoading,
	isProjectsLoading,
	isGuestUser,
	onGuestUserToggle,
	onClientChange,
	onClose,
}: ClientContactModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const isContactMode = mode === 'contact';

	const {
		firstName,
		handleFirstNameChange,
		firstNameError,
		lastName,
		handleLastNameChange,
		lastNameError,
		email,
		handleEmailChange,
		emailError,
		handleEmailBlur,
		jobTitle,
		handleJobTitleChange,
		phone,
		handlePhoneChange,
		handleGuestUserToggle,
		availableProjects,
		selectedProjects,
		handleAddProject,
		handleRemoveProject,
		resetProjects,
		handleSubmit,
	} = useClientContactForm(projectOptions, isGuestUser, onGuestUserToggle);

	const handleClientChange = (newClientId: string) => {
		resetProjects();
		onClientChange(newClientId);
	};

	const title = isContactMode
		? formatMessage({id: 'client_contact.create_modal.title'})
		: formatMessage({id: 'client_contact.guest_user.modal_title'});
	const primaryLabel = isContactMode
		? formatMessage({id: 'client_contact.create_modal.submit_button'})
		: formatMessage({id: 'client_contact.guest_user.submit_button'});

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={onClose}>
			<Modal.Title>{title}</Modal.Title>
			<Modal.Content>
				<div data-cy="client-contact-modal">
					<FlexColumn gap={'l'}>
						<LabelWithTooltipField>
							<ComboBox
								aria-label={formatMessage({id: 'client_contact.field.client'})}
								defaultItems={clientOptions}
								value={clientId}
								onChange={handleClientChange}
								disabled={isClientsLoading}
								placeholder={
									isClientsLoading
										? formatMessage({id: 'client_contact.placeholder.loading_clients'})
										: undefined
								}
								width={'flex'}
							>
								{item => (
									<ComboBox.Item textValue={item.name}>
										<Text noWrap>{item.name}</Text>
									</ComboBox.Item>
								)}
							</ComboBox>
							<FlexRow gap={'xs'} alignItems={'center'}>
								<Label>{formatMessage({id: 'client_contact.field.client'})}</Label>
								<IconTooltip>
									<IconTooltip.Item
										title={formatMessage({id: 'client_contact.tooltip.client.title'})}
										details={formatMessage({id: 'client_contact.tooltip.client'})}
									/>
								</IconTooltip>
							</FlexRow>
						</LabelWithTooltipField>

						<FlexRow gap={'l'}>
							<TextField
								label={formatMessage({id: 'client_contact.field.first_name'})}
								value={firstName}
								onValueChange={handleFirstNameChange}
								hasError={!!firstNameError}
								errorText={firstNameError || undefined}
								size="large"
								width="flex"
							/>
							<TextField
								label={formatMessage({id: 'client_contact.field.last_name'})}
								value={lastName}
								onValueChange={handleLastNameChange}
								hasError={!!lastNameError}
								errorText={lastNameError || undefined}
								size="large"
								width="flex"
							/>
						</FlexRow>

						<TextField
							label={formatMessage({id: 'client_contact.field.email'})}
							value={email}
							onValueChange={handleEmailChange}
							onBlur={handleEmailBlur}
							hasError={!!emailError}
							errorText={emailError || undefined}
							size="large"
							width="flex"
						/>

						<ViewMoreLess
							expandLabel={formatMessage({id: 'client_contact.field.view_more'})}
							collapseLabel={formatMessage({id: 'client_contact.field.view_less'})}
						>
							<FlexColumn gap={'l'}>
								<TextField
									label={formatMessage({id: 'client_contact.field.job_title'})}
									value={jobTitle}
									onValueChange={handleJobTitleChange}
									size="large"
									width="flex"
								/>
								<TextField
									label={formatMessage({id: 'client_contact.field.phone'})}
									value={phone}
									onValueChange={handlePhoneChange}
									size="large"
									width="flex"
								/>
								<Separator />
							</FlexColumn>
						</ViewMoreLess>

						<GuestUserField>
							<Checkbox
								checked={isGuestUser}
								onChange={handleGuestUserToggle}
								aria-label={formatMessage({id: 'client_contact.field.guest_user'})}
							/>
							<Label>{formatMessage({id: 'client_contact.field.guest_user'})}</Label>
							<IconTooltip>
								<IconTooltip.Item
									title={formatMessage({id: 'client_contact.field.guest_user'})}
									details={formatMessage({id: 'client_contact.tooltip.guest_user'})}
								/>
							</IconTooltip>
						</GuestUserField>

						{isGuestUser && (
							<div data-testid="project-access-field">
								<FlexColumn gap={'s'}>
									<LabelWithTooltipField>
										<ComboBox
											aria-label={formatMessage({id: 'client_contact.field.project_access'})}
											defaultItems={availableProjects}
											onChange={handleAddProject}
											disabled={isProjectsLoading}
											placeholder={
												isProjectsLoading
													? formatMessage({id: 'client_contact.placeholder.loading_projects'})
													: formatMessage({id: 'client_contact.placeholder.select_project'})
											}
											width={'flex'}
										>
											{item => (
												<ComboBox.Item textValue={item.name}>
													<FlexRow>
														<Text noWrap>{item.name}</Text>
														<div
															style={{
																marginLeft: 'auto',
																padding: '0.125rem 0.5rem',
																borderRadius: '0.25rem',
																backgroundColor: item.color,
															}}
														>
															<Text>{item.prefix}</Text>
														</div>
													</FlexRow>
												</ComboBox.Item>
											)}
										</ComboBox>
										<FlexRow gap={'xs'} alignItems={'center'}>
											<Label>{formatMessage({id: 'client_contact.field.project_access'})}</Label>
											<IconTooltip>
												<IconTooltip.Item
													title={formatMessage({
														id: 'client_contact.tooltip.project_access.title',
													})}
													details={formatMessage({id: 'client_contact.tooltip.project_access'})}
												/>
											</IconTooltip>
										</FlexRow>
									</LabelWithTooltipField>
									{selectedProjects.map(project => (
										<ProjectListItem key={project.id}>
											<ProjectBadge $color={project.color ?? '#c4c4c4'}>
												{project.prefix ?? 'P'}
											</ProjectBadge>
											<Text noWrap>{project.name}</Text>
											<RemoveButton
												onClick={() => handleRemoveProject(project.id)}
												aria-label={`Remove ${project.name}`}
											>
												<Icon icon={'close'} size={'s'} />
											</RemoveButton>
										</ProjectListItem>
									))}
								</FlexColumn>
							</div>
						)}
					</FlexColumn>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handleSubmit}>{primaryLabel}</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ClientContactModal;
